import React, { useState, useEffect, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Container, Drawer, SimpleGrid, Grid, Card, Title, Text, Stack, Box, Button, Group, Badge, Avatar, ActionIcon, Loader, Anchor, Paper, Tabs, Select } from '@mantine/core';
import { IconMapPin, IconPhone, IconWorld, IconMail, IconRobot, IconBriefcase, IconShoppingCart, IconRocket, IconLanguage, IconCalendarEvent, IconBell, IconEye } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

const AITranslator = ({ text, email }) => {
  const { i18n } = useTranslation();
  const [translatedText, setTranslatedText] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!text) return;
    const targetLang = i18n.language || 'en';
    
    const langMap = { en: 'English', pl: 'Polish', de: 'German', fr: 'French', es: 'Spanish', it: 'Italian', pt: 'Portuguese', nl: 'Dutch', sv: 'Swedish', cs: 'Czech' };
    const langName = langMap[targetLang] || 'English';

    // check cache
    const cacheKey = `ai_transl_${targetLang}_${btoa(encodeURIComponent(String(text).substring(0, 100)))}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      if (cached.includes('Error calling') || cached.includes('RabbitMQ') || cached.includes('null response') || cached.includes('model \'llama3.1\' not found')) {
        localStorage.removeItem(cacheKey);
      } else {
        setTranslatedText(cached);
        return;
      }
    }

    setLoading(true);
    fetch(`${API_BASE_URL}/api/chat/translate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ownerEmail: email, text, targetLanguage: langName })
    }).then(res => res.json()).then(data => {
      if (data.translatedText) {
        setTranslatedText(data.translatedText);
        if (!data.translatedText.includes('Error calling') && !data.translatedText.includes('RabbitMQ') && !data.translatedText.includes('null response')) {
          localStorage.setItem(cacheKey, data.translatedText);
        }
      }
      setLoading(false);
    }).catch(e => {
      console.error(e);
      setLoading(false);
    });

  }, [text, i18n.language, email]);

  if (!text) return null;

  return (
    <Box style={{ position: 'relative' }}>
      {loading && <Loader size="xs" color="indigo" style={{ position: 'absolute', top: -5, right: 0 }} />}
      <Text size="sm" style={{ lineHeight: 1.6, color: '#a1a1aa', whiteSpace: 'pre-wrap' }}>
        {translatedText || String(text)}
      </Text>
    </Box>
  );
};

export default function CompanyProfile({ email: propsEmail, config: propsConfig, services: propsServices, announcements: propsAnnouncements, onAddToCart }) {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState(propsEmail || '');
  const [config, setConfig] = useState(propsConfig || {});
  const [services, setServices] = useState(propsServices || []);
  const [announcements, setAnnouncements] = useState(propsAnnouncements || []);
  const [events, setEvents] = useState([]);
  const [skills, setSkills] = useState([]);
  const [selectedSkillId, setSelectedSkillId] = useState(null);
  const [loading, setLoading] = useState(!propsConfig);
  const [chatOpened, setChatOpened] = useState(false);
  const [pageViews, setPageViews] = useState(0);

  useEffect(() => {
    // If we have a URL token like ?profile=xyz
    const params = new URLSearchParams(window.location.search);
    const pToken = params.get('profile');
    if (pToken && !propsEmail) {
      try {
        const decodedEmail = new String(atob(pToken));
        setEmail(decodedEmail);
      } catch (e) {
        console.error("Invalid profile token", e);
      }
    }
    const sToken = params.get('skill');
    if (sToken) {
      setSelectedSkillId(Number(sToken));
    }
  }, [propsEmail]);

  useEffect(() => {
    if (!email) return;
    
    fetch(`${API_BASE_URL}/api/public/data?ownerEmail=${email}`)
      .then(r => r.json())
      .then(d => { if (d.pageViews) setPageViews(d.pageViews); })
      .catch(e => console.error(e));

    Promise.all([
      propsConfig && Object.keys(propsConfig).length > 0 ? Promise.resolve(propsConfig) : fetch(`${API_BASE_URL}/api/config?ownerEmail=${email}`).then(r => r.json()),
      propsServices && propsServices.length > 0 ? Promise.resolve(propsServices) : fetch(`${API_BASE_URL}/api/services?ownerEmail=${email}`).then(r => r.json()),
      fetch(`${API_BASE_URL}/api/announcements?ownerEmail=${email}`).then(r => {
        if(r.ok) return r.json();
        return [];
      }).catch(() => []),
      fetch(`${API_BASE_URL}/api/events?ownerEmail=${email}`).then(r => {
        if(r.ok) return r.json();
        return [];
      }).catch(() => []),
      fetch(`${API_BASE_URL}/api/skills/my?ownerEmail=${email}`).then(r => {
        if(r.ok) return r.json();
        return [];
      }).catch(() => [])
    ]).then(([configData, servicesData, announcementsData, eventsData, skillsData]) => {
      // If propsConfig is provided (live preview mode), show all skills including PENDING so the user can preview them. Otherwise only show APPROVED.
      const isPreview = propsConfig && Object.keys(propsConfig).length > 0;
      setSkills(Array.isArray(skillsData) ? skillsData.filter(s => isPreview ? true : s.status === 'APPROVED') : []);
      
      let parsedConfig = {};
      if (Array.isArray(configData)) {
          parsedConfig = configData.reduce((acc, curr) => ({ ...acc, [curr.key]: curr.value }), {});
      } else {
          parsedConfig = configData || {};
      }
      setConfig(parsedConfig);

      setServices(Array.isArray(servicesData) ? servicesData : []);
      setAnnouncements(Array.isArray(announcementsData) ? announcementsData : []);
      setEvents(Array.isArray(eventsData) ? eventsData : []);
      setLoading(false);
    }).catch(e => {
      console.error(e);
      setLoading(false);
    });
  }, [email, propsConfig, propsServices]);

  
  if (!email) {
  return (
      <Box style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader size="lg" color="indigo" />
        <Text ml="md">Initializing profile...</Text>
      </Box>
    );
  }


  if (loading) {
  return (
      <Box style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#0a0a0a' }}>
        <Loader size="lg" color="indigo" />
      </Box>
    );
  }

  const companyName = config['company.name'] !== undefined ? config['company.name'] : (email.split('@')[0].toUpperCase());
  const address = config['company.address'] || '';
  const description = config['company.description'] || '';
  const phone = config['company.phone'] || '';
  const website = config['company.website'] || '';

  const hasCustomWidgets = description.includes('{{WIDGET_');

  const renderWidgetChat = () => (
    <Box mb="xl">
      <Paper radius="md" style={{ 
        backgroundColor: '#1a1b1e', border: '1px solid #333', height: '500px', 
        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)', overflow: 'hidden', marginBottom: '16px'
      }}>
        <iframe src={`/?token=${window.btoa(email)}`} style={{ width: '100%', height: '100%', border: 'none' }} title="AI Assistant" />
      </Paper>
    </Box>
  );

  const renderWidgetServices = () => services.filter(s => !s.hidden).length === 0 ? (
    <Paper p="xl" radius="md" style={{ backgroundColor: '#1a1b1e', border: '1px solid #333', textAlign: 'center' }}>
      <IconRobot size={48} color="#94a3b8" style={{ marginBottom: '16px' }} />
      <Text c="dimmed">{t('no_services_listed', 'This company has not listed specific services yet.')}</Text>
    </Paper>
  ) : (
    <Stack gap="md">
      {services.filter(s => !s.hidden).map(srv => (
        <Paper key={srv.id || Math.random()} p="md" radius="md" style={{ backgroundColor: '#1a1b1e', border: '1px solid #333', display: 'flex', flexDirection: 'column' }}>
          <Group justify="space-between" align="flex-start" mb="xs">
            <Title order={5} style={{ color: '#fff' }}>{String(srv.name || srv.title || 'Service')}</Title>
            {srv.price > 0 && (
              <Badge color="green" size="lg" variant="light">{srv.price} {srv.currency || 'PLN'}</Badge>
            )}
          </Group>
          {srv.imageUrl && <img src={srv.imageUrl} alt="service" style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain', marginBottom: '8px', borderRadius: '8px' }} />}
          <AITranslator text={srv.description || ''} email={email} />
          {srv.price > 0 && (
            <Button 
              mt="md" color="green" fullWidth leftSection={<IconShoppingCart size={16} />}
              onClick={() => {
                if (onAddToCart) {
                  onAddToCart({ ownerEmail: email, serviceName: srv.name || srv.title, amount: srv.price, currency: srv.currency || 'PLN' });
                  return;
                }
                const visitorEmail = window.prompt(t('enter_email_for_order', "Podaj swój adres e-mail, na który otrzymasz zamówienie i rachunek:"));
                if (visitorEmail && visitorEmail.trim().includes('@')) {
                  fetch(`${API_BASE_URL}/api/stripe/create-service-checkout`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ ownerEmail: email, visitorEmail: visitorEmail.trim(), serviceName: srv.name || srv.title, amount: srv.price, currency: srv.currency || 'PLN' })
                  })
                  .then(res => res.json())
                  .then(data => { if (data.url) { window.location.href = data.url; } else { alert(t('error_checkout', "Błąd.")); } })
                  .catch(e => { console.error(e); alert(t('error_checkout', "Błąd.")); });
                } else if (visitorEmail) {
                  alert(t('invalid_email', "Nieprawidłowy adres e-mail."));
                }
              }}
            >
              {onAddToCart ? t('add_to_cart', 'Dodaj do koszyka') : t('buy_now', 'Kup teraz')}
            </Button>
          )}
        </Paper>
      ))}
    </Stack>
  );

  const renderWidgetEvents = () => (
    <Stack gap="md">
      {events.map(ev => (
        <Paper key={ev.id} p="md" radius="md" style={{ backgroundColor: '#1a1b1e', border: '1px solid #333', display: 'flex', flexDirection: 'column' }}>
          <Group justify="space-between" mb="xs">
            <Title order={5} style={{ color: '#fff' }}>{String(ev.title)}</Title>
            <Button 
              variant="light" size="xs" color="indigo" leftSection={<IconBell size={16}/>}
              onClick={() => {
                const email = window.prompt("Podaj adres e-mail, na który wyślemy przypomnienie dzień przed wydarzeniem:");
                if (email && email.trim() !== "") {
                  fetch(`${API_BASE_URL}/api/events/${ev.id}/remind`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: email.trim() }) })
                  .then(r => r.json()).then(data => { if (data.status === 'success') alert("Zapisano przypomnienie! Otrzymasz e-mail z powiadomieniem."); else alert("Błąd: " + data.message); })
                  .catch(e => { console.error(e); alert("Wystąpił błąd przy zapisie na przypomnienie."); });
                }
              }}
            >
              Przypomnij mi
            </Button>
          </Group>
          <Text size="sm" c="dimmed" mb="xs">
            <IconCalendarEvent size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            {new Date(ev.eventDate).toLocaleString()}
            <br />
            <IconMapPin size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            {ev.address}
          </Text>
          <AITranslator text={ev.description || ''} email={email} />
        </Paper>
      ))}
    </Stack>
  );

  const renderWidgetJobs = () => (
    <Stack gap="md">
      {announcements.filter(a => a.type === 'JOB').map(job => (
        <Paper key={job.id} p="md" radius="md" style={{ backgroundColor: '#1a1b1e', border: '1px solid #333', display: 'flex', flexDirection: 'column' }}>
          <Group justify="space-between" mb="xs">
            <Title order={5} style={{ color: '#fff' }}>{String(job.title)}</Title>
            <Button component="a" href={`/?job=${job.id}`} target="_blank" variant="light" size="xs" color="indigo">{t('view_details', 'View Details')}</Button>
          </Group>
          <Text size="xs" c="dimmed" mb="sm">{new Date(job.createdAt).toLocaleDateString()}</Text>
          <AITranslator text={job.description || ''} email={email} />
        </Paper>
      ))}
    </Stack>
  );

  const renderWidgetPurchases = () => (
    <Stack gap="md">
      {announcements.filter(a => a.type === 'PURCHASE').map(purchase => (
        <Paper key={purchase.id} p="md" radius="md" style={{ backgroundColor: '#1a1b1e', border: '1px solid #333', display: 'flex', flexDirection: 'column' }}>
          <Title order={5} style={{ color: '#fff' }} mb="xs">{String(purchase.title)}</Title>
          <Text size="xs" c="dimmed" mb="sm">{new Date(purchase.createdAt).toLocaleDateString()}</Text>
          <AITranslator text={purchase.description || ''} email={email} />
        </Paper>
      ))}
    </Stack>
  );

  const renderWidgetMap = () => config['company.map.lat'] && config['company.map.lng'] ? (
    <Paper radius="md" style={{ height: '350px', border: '1px solid #333', overflow: 'hidden' }}>
      <iframe 
        width="100%" height="100%" frameBorder="0" scrolling="no" marginHeight="0" marginWidth="0" 
        src={`https://www.openstreetmap.org/export/embed.html?bbox=${parseFloat(config['company.map.lng'])-0.01}%2C${parseFloat(config['company.map.lat'])-0.01}%2C${parseFloat(config['company.map.lng'])+0.01}%2C${parseFloat(config['company.map.lat'])+0.01}&layer=mapnik&marker=${config['company.map.lat']}%2C${config['company.map.lng']}`} 
        style={{ border: 'none' }}
      ></iframe>
    </Paper>
  ) : <Text c="dimmed">Map coordinates not provided.</Text>;

  if (config['template.html'] && config['template.html'].trim().length > 0) {
    return (
      <div style={{ width: '100%', height: '100vh', margin: 0, padding: 0, overflow: 'hidden' }}>
        <iframe
          src={`${API_BASE_URL}/api/public/preview/${window.btoa(email)}`}
          style={{ width: '100%', height: '100%', border: 'none' }}
          title="Company Profile Template"
        />
      </div>
    );
  }

  return (
    <Box style={{ minHeight: '100vh', backgroundColor: '#0a0a0a' }}>
      {/* Header / Hero */}
      <Box style={{ 
        backgroundColor: '#000000', 
        backgroundImage: config['company.header.image'] ? `url(${config['company.header.image']})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        padding: '16px 20px', 
        color: 'white', 
        textAlign: 'center',
        position: 'relative'
      }}>
        {config['company.header.image'] && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.4)' }} />}
        
        <Box style={{ position: 'absolute', top: 10, right: 20, zIndex: 10 }}>
          <Group gap="sm">
            <Button 
              color="indigo" 
              size="xs" 
              radius="xl" 
              leftSection={<IconRobot size={16} />} 
              onClick={() => setChatOpened(true)}
              style={{ boxShadow: '0 4px 15px rgba(79, 70, 229, 0.4)' }}
            >
              {t('chat_with_ai', 'Zapytaj AI o naszą ofertę')}
            </Button>
            <Select 
              size="xs"
              value={i18n.language?.substring(0, 2) || 'en'}
              onChange={(val) => i18n.changeLanguage(val)}
              data={[
                { value: 'en', label: 'EN' },
                { value: 'pl', label: 'PL' },
                { value: 'de', label: 'DE' },
                { value: 'fr', label: 'FR' },
                { value: 'es', label: 'ES' },
                { value: 'it', label: 'IT' },
                { value: 'pt', label: 'PT' },
                { value: 'nl', label: 'NL' },
                { value: 'sv', label: 'SV' },
                { value: 'cs', label: 'CS' }
              ]}
              style={{ width: '80px' }}
            />
          </Group>
        </Box>

        <Container size="lg" style={{ position: 'relative', zIndex: 1 }}>
          {config['company.logo'] && (
            <Avatar size={40} radius={100} mx="auto" mb="xs" src={String(config['company.logo'])} />
          )}
          {companyName && (
            <Title order={3} mb="xs" style={{ fontWeight: 800 }}>{String(companyName)}</Title>
          )}
          <Group justify="center" mt="xs" gap="sm">
            {address && (
              <Group gap="xs"><IconMapPin size={16}/><Text size="sm">{String(address)}</Text></Group>
            )}
            {phone && (
              <Group gap="xs"><IconPhone size={16}/><Text size="sm">{String(phone)}</Text></Group>
            )}
            {website && (
              <Group gap="xs"><IconWorld size={16}/><Anchor size="sm" href={String(website).startsWith('http') ? String(website) : 'https://'+String(website)} target="_blank" c="white">{String(website)}</Anchor></Group>
            )}
          </Group>

        </Container>
      </Box>

      <Drawer
        opened={chatOpened}
        onClose={() => setChatOpened(false)}
        position="right"
        size="lg"
        title={<Group><IconRobot size={24} color="#6366f1" /><Title order={4} c="white">AI Assistant</Title></Group>}
        styles={{
          content: { backgroundColor: '#1a1b1e' },
          header: { backgroundColor: '#1a1b1e', borderBottom: '1px solid #333' },
          close: { color: 'white' }
        }}
      >
        <Box style={{ height: 'calc(100vh - 60px)' }}>
          <iframe 
            src={`/?token=${window.btoa(email)}`} 
            style={{ width: '100%', height: '100%', border: 'none' }}
            title="AI Assistant"
          />
        </Box>
      </Drawer>


      <Container fluid px={{ base: 'md', md: 'xl' }} py="xl">
        {hasCustomWidgets ? (
          <Box pt="xl">
            {description.split(/(\{\{WIDGET_[A-Z]+\}\})/g).map((part, i) => {
              if (part === '{{WIDGET_CHAT}}') return <React.Fragment key={i}>{renderWidgetChat()}</React.Fragment>;
              if (part === '{{WIDGET_SERVICES}}') return <React.Fragment key={i}>{renderWidgetServices()}</React.Fragment>;
              if (part === '{{WIDGET_EVENTS}}') return <React.Fragment key={i}>{renderWidgetEvents()}</React.Fragment>;
              if (part === '{{WIDGET_JOBS}}') return <React.Fragment key={i}>{renderWidgetJobs()}</React.Fragment>;
              if (part === '{{WIDGET_PURCHASES}}') return <React.Fragment key={i}>{renderWidgetPurchases()}</React.Fragment>;
              if (part === '{{WIDGET_MAP}}') return <React.Fragment key={i}>{renderWidgetMap()}</React.Fragment>;
              return <div key={i} dangerouslySetInnerHTML={{ __html: part }} />;
            })}
          </Box>
        ) : (
        <React.Fragment>
        <Grid gutter="xl">


          {/* Main Content (Services & About & Announcements) */}
          <Grid.Col span={{ base: 12, md: 12 }}>
            <Tabs defaultValue="skills" color="indigo" variant="outline" mb="xl">
              <Tabs.List> 
                <Tabs.Tab value="skills" leftSection={<IconRobot size={16}/>}>AI Skills</Tabs.Tab>
                <Tabs.Tab value="services" leftSection={<IconRocket size={16}/>}>{t('our_services', 'Our Services')}</Tabs.Tab> 
                {events.length > 0 && (
                  <Tabs.Tab value="events" leftSection={<IconCalendarEvent size={16}/>}>Wydarzenia</Tabs.Tab> 
                )}
                {announcements.filter(a => a.type === 'JOB').length > 0 && ( 
                  <Tabs.Tab value="jobs" leftSection={<IconBriefcase size={16}/>}>{t('tab_job_offers', 'Job Offers')}</Tabs.Tab> 
                )} 
                {announcements.filter(a => a.type === 'PURCHASE').length > 0 && ( 
                  <Tabs.Tab value="purchases" leftSection={<IconShoppingCart size={16}/>}>{t('tab_needs', 'Needs')}</Tabs.Tab> 
                )} 
              </Tabs.List>

              
              <Tabs.Panel value="skills" pt="md">
                {skills.length === 0 ? (
                  <Paper p="xl" radius="md" style={{ backgroundColor: '#1a1b1e', border: '1px solid #333', textAlign: 'center' }}>
                    <IconRobot size={48} color="#94a3b8" style={{ marginBottom: '16px' }} />
                    <Text c="dimmed">{t('no_skills_listed', 'Sprzedawca nie wystawił jeszcze żadnych Skilli AI.')}</Text>
                  </Paper>
                ) : (
                  <Grid gutter="xl">
                    <Grid.Col span={{ base: 12, md: 4, lg: 3 }}>
                      <Paper p="sm" radius="md" style={{ backgroundColor: '#1a1b1e', border: '1px solid #333' }}>
                        <Title order={6} mb="sm" c="gray.4" px="xs">Katalog Skilli</Title>
                        <Stack gap={4}>
                          {skills.map(skill => (
                            <Button 
                              key={skill.id}
                              variant={selectedSkillId === skill.id ? "light" : "subtle"}
                              color={selectedSkillId === skill.id ? "indigo" : "gray"}
                              justify="flex-start"
                              fullWidth
                              onClick={() => {
                                setSelectedSkillId(skill.id);
                                const url = new URL(window.location.href);
                                url.searchParams.set('skill', skill.id);
                                window.history.pushState({}, '', url);
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                              }}
                              style={{ textAlign: 'left', height: 'auto', padding: '12px 12px' }}
                            >
                              <Box>
                                <Text size="sm" fw={600} lineClamp={2} style={{ whiteSpace: 'normal' }}>{skill.title}</Text>
                                <Text size="xs" c="dimmed" mt={4}>{skill.price} EUR</Text>
                              </Box>
                            </Button>
                          ))}
                        </Stack>
                      </Paper>
                    </Grid.Col>
                    
                    <Grid.Col span={{ base: 12, md: 8, lg: 9 }}>
                      {(() => {
                        const activeSkill = selectedSkillId ? skills.find(s => s.id === selectedSkillId) : skills[0];
                        if (!activeSkill) return null;

                        return (
                          <Paper p="xl" radius="md" style={{ backgroundColor: '#1a1b1e', border: '1px solid #333', display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
                            <Group justify="space-between" align="flex-start" mb="xl">
                              <Title order={3} style={{ color: '#fff' }}>{activeSkill.title}</Title>
                              <Badge color="indigo" size="xl" variant="filled">{activeSkill.price} EUR</Badge>
                            </Group>
                            
                            {activeSkill.videoUrl && (
                              <Box style={{ 
                                width: '100%', 
                                maxWidth: '380px', 
                                aspectRatio: '9/16', 
                                margin: '0 auto 32px auto',
                                overflow: 'hidden', 
                                borderRadius: 16, 
                                backgroundColor: '#000', 
                                position: 'relative',
                                boxShadow: '0 8px 32px rgba(0,0,0,0.5)'
                              }}>
                                {activeSkill.videoUrl.includes('youtube.com') || activeSkill.videoUrl.includes('youtu.be') ? (
                                  <iframe 
                                    src={activeSkill.videoUrl.replace('watch?v=', 'embed/').replace('shorts/', 'embed/') + '?autoplay=1&mute=1&loop=1'} 
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    style={{ width: '100%', height: '100%', border: 'none', pointerEvents: 'none' }}
                                  />
                                ) : (
                                  (activeSkill.videoUrl?.toLowerCase().endsWith('.jpg') || activeSkill.videoUrl?.toLowerCase().endsWith('.jpeg') || activeSkill.videoUrl?.toLowerCase().endsWith('.png')) ? (
                                    <img 
                                      src={activeSkill.videoUrl?.startsWith('/') ? `${API_BASE_URL}${activeSkill.videoUrl}` : activeSkill.videoUrl} 
                                      alt="Skill preview"
                                      style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                                    />
                                  ) : (
                                    <video 
                                      src={activeSkill.videoUrl?.startsWith('/') ? `${API_BASE_URL}${activeSkill.videoUrl}` : activeSkill.videoUrl} 
                                      autoPlay={true}
                                      playsInline={true}
                                      loop={true}
                                      muted={true}
                                      style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                                    />
                                  )
                                )}
                                <Box style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, cursor: 'pointer' }} onClick={() => window.open(activeSkill.videoUrl?.startsWith('/') ? `${API_BASE_URL}${activeSkill.videoUrl}` : activeSkill.videoUrl, '_blank')}></Box>
                              </Box>
                            )}
                            
                            <Text size="md" c="gray.3" mb="xl" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{activeSkill.description}</Text>
                            
                            <Group gap="xs" mb="xl">
                              {activeSkill.tags && activeSkill.tags.split(',').map(tag => (
                                <Badge key={tag} color="gray" variant="outline" size="md">{tag.trim()}</Badge>
                              ))}
                            </Group>
                            
                            <Button 
                              size="lg"
                              mt="auto"
                              color="indigo" leftSection={<IconShoppingCart size={20} />}
                              onClick={() => {
                                const visitorEmail = window.prompt(t('enter_email_for_order', "Podaj swój adres e-mail, na który otrzymasz dostęp do Skilla:"));
                                if (visitorEmail && visitorEmail.trim().includes('@')) {
                                  fetch(`${API_BASE_URL}/api/orders/public/create-skill-session`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ skillId: activeSkill.id, visitorEmail: visitorEmail.trim() })
                                  })
                                  .then(res => res.json())
                                  .then(data => { if (data.status === 'SUCCESS') { alert(t('order_placed_success', "Zamówienie zostało pomyślnie złożone! Autor skontaktuje się z Tobą.")); } else { alert(t('error_checkout', "Błąd zamówienia.")); } })
                                  .catch(e => { console.error(e); alert(t('error_checkout', "Błąd.")); });
                                } else if (visitorEmail) {
                                  alert(t('invalid_email', "Nieprawidłowy adres e-mail."));
                                }
                              }}
                              style={{ alignSelf: 'flex-start' }}
                            >
                              {t('buy_skill', 'Kup Skilla')}
                            </Button>
                          </Paper>
                        );
                      })()}
                    </Grid.Col>
                  </Grid>
                )}
              </Tabs.Panel>

              <Tabs.Panel value="services" pt="md">
                {services.filter(s => !s.hidden).length === 0 ? (
                  <Paper p="xl" radius="md" style={{ backgroundColor: '#1a1b1e', border: '1px solid #333', textAlign: 'center' }}>
                    <IconRobot size={48} color="#94a3b8" style={{ marginBottom: '16px' }} />
                    <Text c="dimmed">{t('no_services_listed', 'This company has not listed specific services yet.')}</Text>
                  </Paper>
                ) : (
                  <Stack gap="md">
                    {services.filter(s => !s.hidden).map(srv => (
                      <Paper key={srv.id || Math.random()} p="md" radius="md" style={{ backgroundColor: '#1a1b1e', border: '1px solid #333', display: 'flex', flexDirection: 'column' }}>
                        <Group justify="space-between" align="flex-start" mb="xs">
                          <Title order={5} style={{ color: '#fff' }}>{String(srv.name || srv.title || 'Service')}</Title>
                          {srv.price > 0 && (
                            <Badge color="green" size="lg" variant="light">{srv.price} {srv.currency || 'PLN'}</Badge>
                          )}
                        </Group>
                        {srv.imageUrl && <img src={srv.imageUrl} alt="service" style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain', marginBottom: '8px', borderRadius: '8px' }} />}
                        <AITranslator text={srv.description || ''} email={email} />
                        {srv.price > 0 && (
                          <Button 
                            mt="md" 
                            color="green" 
                            fullWidth 
                            leftSection={<IconShoppingCart size={16} />}
                            onClick={() => {
                              if (onAddToCart) {
                                onAddToCart({
                                  ownerEmail: email,
                                  serviceName: srv.name || srv.title,
                                  amount: srv.price,
                                  currency: srv.currency || 'PLN'
                                });
                                return;
                              }
                              const visitorEmail = window.prompt(t('enter_email_for_order', "Podaj swój adres e-mail, na który otrzymasz zamówienie i rachunek:"));
                              if (visitorEmail && visitorEmail.trim().includes('@')) {
                                fetch(`${API_BASE_URL}/api/stripe/create-service-checkout`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({
                                    ownerEmail: email,
                                    visitorEmail: visitorEmail.trim(),
                                    serviceName: srv.name || srv.title,
                                    amount: srv.price,
                                    currency: srv.currency || 'PLN'
                                  })
                                })
                                .then(res => res.json())
                                .then(data => {
                                  if (data.url) {
                                    window.location.href = data.url;
                                  } else {
                                    alert(t('error_checkout', "Błąd: Sprzedawca nie skonfigurował jeszcze płatności online."));
                                  }
                                })
                                .catch(e => {
                                  console.error(e);
                                  alert(t('error_checkout', "Błąd: Sprzedawca nie skonfigurował jeszcze płatności online."));
                                });
                              } else if (visitorEmail) {
                                alert(t('invalid_email', "Nieprawidłowy adres e-mail."));
                              }
                            }}
                          >
                            {onAddToCart ? t('add_to_cart', 'Dodaj do koszyka') : t('buy_now', 'Kup teraz')}
                          </Button>
                        )}
                      </Paper>
                    ))}
                  </Stack>
                )}
              </Tabs.Panel>
              
              <Tabs.Panel value="events" pt="md">
                <Stack gap="md">
                  {events.map(ev => (
                    <Paper key={ev.id} p="md" radius="md" style={{ backgroundColor: '#1a1b1e', border: '1px solid #333', display: 'flex', flexDirection: 'column' }}>
                      <Group justify="space-between" mb="xs">
                        <Title order={5} style={{ color: '#fff' }}>{String(ev.title)}</Title>
                        <Button 
                          variant="light" size="xs" color="indigo" leftSection={<IconBell size={16}/>}
                          onClick={() => {
                            const email = window.prompt("Podaj adres e-mail, na który wyślemy przypomnienie dzień przed wydarzeniem:");
                            if (email && email.trim() !== "") {
                              fetch(`${API_BASE_URL}/api/events/${ev.id}/remind`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ email: email.trim() })
                              }).then(r => r.json()).then(data => {
                                if (data.status === 'success') alert("Zapisano przypomnienie! Otrzymasz e-mail z powiadomieniem.");
                                else alert("Błąd: " + data.message);
                              }).catch(e => {
                                console.error(e);
                                alert("Wystąpił błąd przy zapisie na przypomnienie.");
                              });
                            }
                          }}
                        >
                          Przypomnij mi
                        </Button>
                      </Group>
                      <Text size="sm" c="dimmed" mb="xs">
                        <IconCalendarEvent size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                        {new Date(ev.eventDate).toLocaleString()}
                        <br />
                        <IconMapPin size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                        {ev.address}
                      </Text>
                      <AITranslator text={ev.description || ''} email={email} />
                    </Paper>
                  ))}
                </Stack>
              </Tabs.Panel>

              <Tabs.Panel value="jobs" pt="md">
                <Stack gap="md">
                  {announcements.filter(a => a.type === 'JOB').map(job => (
                    <Paper key={job.id} p="md" radius="md" style={{ backgroundColor: '#1a1b1e', border: '1px solid #333', display: 'flex', flexDirection: 'column' }}>
                      <Group justify="space-between" mb="xs">
                        <Title order={5} style={{ color: '#fff' }}>{String(job.title)}</Title>
                        <Button component="a" href={`/?job=${job.id}`} target="_blank" variant="light" size="xs" color="indigo">{t('view_details', 'View Details')}</Button>
                      </Group>
                      <Text size="xs" c="dimmed" mb="sm">{new Date(job.createdAt).toLocaleDateString()}</Text>
                      <AITranslator text={job.description || ''} email={email} />
                    </Paper>
                  ))}
                </Stack>
              </Tabs.Panel>

              <Tabs.Panel value="purchases" pt="md">
                <Stack gap="md">
                  {announcements.filter(a => a.type === 'PURCHASE').map(purchase => (
                    <Paper key={purchase.id} p="md" radius="md" style={{ backgroundColor: '#1a1b1e', border: '1px solid #333', display: 'flex', flexDirection: 'column' }}>
                      <Title order={5} style={{ color: '#fff' }} mb="xs">{String(purchase.title)}</Title>
                      <Text size="xs" c="dimmed" mb="sm">{new Date(purchase.createdAt).toLocaleDateString()}</Text>
                      <AITranslator text={purchase.description || ''} email={email} />
                    </Paper>
                  ))}
                </Stack>
              </Tabs.Panel>
            </Tabs>
            </Grid.Col>
        </Grid>
        
        {config['company.map.lat'] && config['company.map.lng'] ? (
          <Grid gutter="xl" mt={40}>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Title order={3} mb="md" style={{ color: '#fff' }}>{t('map_location', 'Map & Location')}</Title>
              {config['company.map.lat'] && config['company.map.lng'] ? (
                <Paper radius="md" style={{ height: '350px', border: '1px solid #333', overflow: 'hidden' }}>
                  <iframe 
                    width="100%" 
                    height="100%" 
                    frameBorder="0" 
                    scrolling="no" 
                    marginHeight="0" 
                    marginWidth="0" 
                    src={`https://www.openstreetmap.org/export/embed.html?bbox=${parseFloat(config['company.map.lng'])-0.01}%2C${parseFloat(config['company.map.lat'])-0.01}%2C${parseFloat(config['company.map.lng'])+0.01}%2C${parseFloat(config['company.map.lat'])+0.01}&layer=mapnik&marker=${config['company.map.lat']}%2C${config['company.map.lng']}`} 
                    style={{ border: 'none' }}
                  ></iframe>
                </Paper>
              ) : (
                <Text c="dimmed">Map coordinates not provided.</Text>
              )}
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Title order={3} mb="md" style={{ color: '#fff' }}>{t('about_us', 'About Us')}</Title>
              <Paper p="xl" radius="md" style={{ backgroundColor: '#1a1b1e', border: '1px solid #333', minHeight: '350px' }}>
                <Box>
                  <AITranslator text={description} email={email} />
                </Box>
              </Paper>
            </Grid.Col>
          </Grid>
        ) : (
          <Grid gutter="xl" mt={40}>
            <Grid.Col span={12}>
              <Title order={3} mb="md" style={{ color: '#fff' }}>{t('about_us', 'About Us')}</Title>
              <Paper p="xl" radius="md" style={{ backgroundColor: '#1a1b1e', border: '1px solid #333' }}>
                 <AITranslator text={description} email={email} />
              </Paper>
            </Grid.Col>
          </Grid>
        )}

        </React.Fragment>
        )}

        {config['company.affiliate.enabled'] === 'true' && config['company.affiliate.code'] && (
          <Box mt={60} pt="xl" style={{ borderTop: '1px solid rgba(0,0,0,0.1)', textAlign: 'center' }}>
            <Text size="sm" c="dimmed" mb="xs">{t('partner_program', 'Partner AILinkB - Polecaj i zarabiaj!')}</Text>
            <Group justify="center">
              <Badge color="indigo" size="lg" variant="light">{t('affiliate_code', 'Kod Affiliate:')} {config['company.affiliate.code']}</Badge>
              <Button size="xs" variant="outline" color="indigo" onClick={() => window.open(`/?register=true&affiliate=${config['company.affiliate.code']}`, '_blank')}>{t('create_free_account', 'Zamów konto')}</Button>
            </Group>
          </Box>
        )}

        <Box mt={60} pb="xl" style={{ textAlign: 'center' }}>
          <Group justify="center" gap="xs">
            <Text size="xs" c="dimmed">© {new Date().getFullYear()} AILinkB. QR-SYSTEM sp. z.o.o. Płaza ul. Graniczna 13B Kapitał zakładowy 10 000 PLN.</Text>
            <Anchor size="xs" c="dimmed" href="/terms" target="_blank" underline="always">
              {t('privacy_policy_title')} & Cookies
            </Anchor>
          </Group>
        </Box>

      </Container>
    </Box>
  );
}


